import { type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive: "border-transparent bg-destructive text-destructive-foreground",
        outline: "text-foreground",
        success: "border-transparent bg-emerald-100 text-emerald-800",
        warning: "border-transparent bg-amber-100 text-amber-800",
        danger: "border-transparent bg-red-100 text-red-800",
        // ADR-0009 prod test partition. Deliberately the only DASHED variant
        // and the only one on the violet ramp: a TEST pill sits next to a
        // status pill (ACTIVE / PENDING) and must never be mistaken for one.
        // It marks *which partition* an entity lives in, not what state it's in.
        test: "border-dashed border-violet-400 bg-violet-100 text-violet-800",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type BadgeVariantProps = VariantProps<typeof badgeVariants>;

export function Badge({
  className,
  variant,
  ...props
}: HTMLAttributes<HTMLDivElement> & BadgeVariantProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}
